import { useEffect, useState } from "react";
import { Plus, Loader2, Zap, Save, UserPlus, Search, Award, Gift, TrendingUp, Play, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import apiClient, { getErrorMessage } from "@/lib/apiClient";
import { AdminHeader, TableWrap, Th, Td, Tr, StatusChip, Spinner, EmptyRow, Modal, Field } from "@/components/admin/ui";

const TYPES = [
  { value: "free_session", label: "Free Session" },
  { value: "discount", label: "Account Credit" },
  { value: "tournament_entry", label: "Tournament Entry" },
  { value: "vip_package", label: "VIP Package" },
  { value: "gift_card", label: "Gift Card" },
  { value: "merchandise", label: "Merchandise" },
];

const EARN_GROUPS = [
  {
    label: "Signup & Bookings",
    fields: [
      { key: "signup_bonus", label: "Signup bonus" },
      { key: "first_booking_bonus", label: "First booking bonus" },
      { key: "subsequent_booking_bonus", label: "Subsequent booking bonus" },
      { key: "group_booking_bonus", label: "Group booking (2+ stations)" },
      { key: "booking_per_dollar", label: "Points per $1 booked", step: "0.1" },
    ],
  },
  {
    label: "Tournaments",
    fields: [
      { key: "tournament_signup", label: "Tournament sign-up" },
      { key: "tournament_first", label: "1st place" },
      { key: "tournament_second", label: "2nd place" },
      { key: "tournament_third", label: "3rd place" },
    ],
  },
  {
    label: "Sim & Track Leaderboards",
    fields: [
      { key: "personal_best_lap", label: "Personal best lap" },
      { key: "lap_board_first", label: "Track board #1" },
      { key: "lap_board_second", label: "Track board #2" },
      { key: "lap_board_third", label: "Track board #3" },
      { key: "sim_base", label: "Sim session base pts" },
      { key: "sim_per_lap", label: "Sim points per lap" },
      { key: "sim_cap", label: "Sim session cap" },
    ],
  },
];

const CLAIM_FIELDS = [
  { key: "booking_credit_cost", label: "Booking credit (30 min)" },
  { key: "tournament_entry_cost", label: "Free tournament entry" },
  { key: "gift_card_5_cost", label: "Gift card per $5" },
  { key: "membership_basic_cost", label: "Membership · Basic (30 days)" },
  { key: "membership_pro_cost", label: "Membership · Pro (30 days)" },
  { key: "membership_champ_cost", label: "Membership · Champ (30 days)" },
  { key: "membership_vip_cost", label: "Membership · VIP (30 days)" },
];

const emptyForm = { title: "", description: "", points_required: 500, reward_type: "free_session", value: 0, stock_quantity: "", is_active: true };

const previousMonthKey = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function AdminRewards() {
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [rules, setRules] = useState(null);
  const [savingRules, setSavingRules] = useState(false);

  const [tiers, setTiers] = useState(null);
  const [savingTiers, setSavingTiers] = useState(false);

  const [claim, setClaim] = useState(null);
  const [savingClaim, setSavingClaim] = useState(false);

  const [loyaltyMonth, setLoyaltyMonth] = useState(previousMonthKey());
  const [runningLoyalty, setRunningLoyalty] = useState(false);

  const [grantOpen, setGrantOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [userQuery, setUserQuery] = useState("");
  const [grant, setGrant] = useState({ user_id: "", points: 500, reason: "" });
  const [granting, setGranting] = useState(false);

  const load = () => {
    Promise.all([
      apiClient.get("/rewards"),
      apiClient.get("/admin/earn-rules"),
      apiClient.get("/admin/loyalty-tiers"),
      apiClient.get("/admin/claim-rules"),
    ])
      .then(([rw, er, lt, cr]) => { setRewards(rw.data); setRules(er.data); setTiers(lt.data); setClaim(cr.data); })
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const create = async () => {
    if (!form.title || !form.description) { toast.error("Title and description are required"); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title, description: form.description,
        points_required: Number(form.points_required), reward_type: form.reward_type,
        value: Number(form.value), is_active: form.is_active,
        stock_quantity: form.stock_quantity === "" ? null : Number(form.stock_quantity),
      };
      if (editing) {
        await apiClient.put(`/admin/rewards/${editing.id}`, payload);
        toast.success("Reward updated");
      } else {
        await apiClient.post("/admin/rewards", payload);
        toast.success("Reward created");
      }
      setModalOpen(false); setEditing(null); setForm(emptyForm); load();
    } catch (err) { toast.error(getErrorMessage(err, "Save failed")); } finally { setSaving(false); }
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      title: r.title, description: r.description, points_required: r.points_required,
      reward_type: r.reward_type, value: r.value, is_active: r.is_active,
      stock_quantity: r.stock_quantity == null ? "" : r.stock_quantity,
    });
    setModalOpen(true);
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.title}"?`)) return;
    try {
      await apiClient.delete(`/admin/rewards/${r.id}`);
      toast.success("Reward deleted");
      load();
    } catch (err) { toast.error(getErrorMessage(err, "Delete failed")); }
  };

  const saveRules = async () => {
    setSavingRules(true);
    try {
      const payload = {};
      EARN_GROUPS.flatMap((g) => g.fields).forEach(({ key }) => { payload[key] = Number(rules[key]); });
      const { data } = await apiClient.put("/admin/earn-rules", payload);
      setRules(data);
      toast.success("Earn rules updated");
    } catch (err) { toast.error(getErrorMessage(err, "Update failed")); } finally { setSavingRules(false); }
  };

  const saveTiers = async () => {
    setSavingTiers(true);
    try {
      const payload = { tiers: tiers.map((t) => ({ tier: Number(t.tier), min_bookings: Number(t.min_bookings), points: Number(t.points) })) };
      const { data } = await apiClient.put("/admin/loyalty-tiers", payload);
      setTiers(data);
      toast.success("Loyalty tiers updated");
    } catch (err) { toast.error(getErrorMessage(err, "Update failed")); } finally { setSavingTiers(false); }
  };

  const saveClaim = async () => {
    setSavingClaim(true);
    try {
      const payload = {};
      CLAIM_FIELDS.forEach(({ key }) => { payload[key] = Number(claim[key]); });
      const { data } = await apiClient.put("/admin/claim-rules", payload);
      setClaim(data);
      toast.success("Claim rules updated");
    } catch (err) { toast.error(getErrorMessage(err, "Update failed")); } finally { setSavingClaim(false); }
  };

  const runLoyalty = async () => {
    setRunningLoyalty(true);
    try {
      const { data } = await apiClient.post("/admin/loyalty/run", null, { params: { month: loyaltyMonth } });
      toast.success(`Loyalty ${data.month}: ${data.awarded} awarded, ${data.skipped} skipped`);
    } catch (err) { toast.error(getErrorMessage(err, "Loyalty run failed")); } finally { setRunningLoyalty(false); }
  };

  const openGrant = () => {
    setGrant({ user_id: "", points: 500, reason: "" });
    setUserQuery("");
    setGrantOpen(true);
    if (users.length === 0) apiClient.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});
  };

  const doGrant = async () => {
    if (!grant.user_id) { toast.error("Select a user"); return; }
    if (!Number(grant.points)) { toast.error("Enter a non-zero point amount"); return; }
    setGranting(true);
    try {
      await apiClient.post(`/admin/users/${grant.user_id}/grant-points`, {
        points: Number(grant.points), reason: grant.reason || "Admin points grant",
      });
      toast.success("Points granted");
      setGrantOpen(false);
    } catch (err) { toast.error(getErrorMessage(err, "Grant failed")); } finally { setGranting(false); }
  };

  const filteredUsers = users.filter((u) => {
    const q = userQuery.toLowerCase();
    return !q || `${u.first_name} ${u.last_name}`.toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
  }).slice(0, 30);

  if (loading) return <Spinner label="Loading rewards" />;

  return (
    <div>
      <AdminHeader title="Rewards" subtitle="Earn rules · Loyalty tiers · Claim costs · Custom promotions"
        right={<div className="flex gap-2">
          <button onClick={openGrant} className="neon-btn px-5 py-2.5" data-testid="grant-points-open" style={{ background: "linear-gradient(90deg, rgba(0,255,157,0.16), rgba(0,229,255,0.12))", borderColor: "rgba(0,255,157,0.4)" }}><UserPlus className="w-4 h-4" /> Grant Points</button>
          <button onClick={() => { setEditing(null); setForm(emptyForm); setModalOpen(true); }} className="neon-btn px-5 py-2.5" data-testid="add-reward"><Plus className="w-4 h-4" /> Add Promo Reward</button>
        </div>} />

      {/* Earn rules */}
      {rules && (
        <div className="glass rounded-2xl p-6 mb-6" data-testid="earn-rules-panel">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-display text-lg font-bold text-white flex items-center gap-2"><Zap className="w-5 h-5" style={{ color: "#ffb020" }} /> Point Earning Rules</h3>
          </div>
          <p className="text-slate-400 text-sm mb-5">How many points customers earn for each event. Changes apply to new bookings, sim laps, tournament placements, and leaderboard leadership.</p>
          {EARN_GROUPS.map((g) => (
            <div key={g.label} className="mb-4">
              <p className="text-slate-300 text-xs font-semibold uppercase tracking-wider mb-2">{g.label}</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {g.fields.map(({ key, label, step }) => (
                  <Field key={key} label={label}>
                    <input type="number" min="0" step={step || "1"} className="input-cyber" value={rules[key] ?? 0} onChange={(e) => setRules({ ...rules, [key]: e.target.value })} data-testid={`earn-rule-${key}`} />
                  </Field>
                ))}
              </div>
            </div>
          ))}
          <button onClick={saveRules} disabled={savingRules} className="neon-btn px-6 py-2.5" data-testid="earn-rules-save">
            {savingRules ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : <><Save className="w-4 h-4" /> Save Earn Rules</>}
          </button>
        </div>
      )}

      {/* Loyalty tiers */}
      {tiers && (
        <div className="glass rounded-2xl p-6 mb-6" data-testid="loyalty-panel">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-display text-lg font-bold text-white flex items-center gap-2"><Award className="w-5 h-5" style={{ color: "#00ff9d" }} /> Loyalty (monthly)</h3>
          </div>
          <p className="text-slate-400 text-sm mb-4">Members earn bonus points once per month based on how many bookings they made. Highest tier wins (not stacked).</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {tiers.map((t, i) => (
              <div key={t.tier} className="p-4 rounded-xl" style={{ background: "rgba(8,11,22,0.6)", border: "1px solid rgba(120,150,220,0.14)" }}>
                <p className="text-white font-display font-bold mb-3">Tier {t.tier}</p>
                <Field label="Min bookings this month">
                  <input type="number" min="1" className="input-cyber" value={t.min_bookings} onChange={(e) => setTiers(tiers.map((x, xi) => xi === i ? { ...x, min_bookings: e.target.value } : x))} data-testid={`loyalty-tier-${t.tier}-min`} />
                </Field>
                <Field label="Points awarded">
                  <input type="number" min="0" className="input-cyber" value={t.points} onChange={(e) => setTiers(tiers.map((x, xi) => xi === i ? { ...x, points: e.target.value } : x))} data-testid={`loyalty-tier-${t.tier}-points`} />
                </Field>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-4">
            <button onClick={saveTiers} disabled={savingTiers} className="neon-btn px-6 py-2.5" data-testid="loyalty-save">
              {savingTiers ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : <><Save className="w-4 h-4" /> Save Tiers</>}
            </button>
            <div className="flex items-center gap-2 ml-auto">
              <input type="month" className="input-cyber" value={loyaltyMonth} onChange={(e) => setLoyaltyMonth(e.target.value)} data-testid="loyalty-run-month" style={{ width: "10rem" }} />
              <button onClick={runLoyalty} disabled={runningLoyalty} className="neon-outline-btn px-4 py-2" data-testid="loyalty-run-btn">
                {runningLoyalty ? <><Loader2 className="w-4 h-4 animate-spin" /> Running...</> : <><Play className="w-4 h-4" /> Run for month</>}
              </button>
            </div>
          </div>
          <p className="text-slate-500 text-xs mt-2">The &ldquo;Run for month&rdquo; button is idempotent — users already awarded for that month are skipped. Defaults to the previous month.</p>
        </div>
      )}

      {/* Claim rules */}
      {claim && (
        <div className="glass rounded-2xl p-6 mb-8" data-testid="claim-rules-panel">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-display text-lg font-bold text-white flex items-center gap-2"><Gift className="w-5 h-5" style={{ color: "#ff2e97" }} /> Point Claim Costs</h3>
          </div>
          <p className="text-slate-400 text-sm mb-4">Set how many points each redemption costs. Customers see live prices on their Rewards page.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {CLAIM_FIELDS.map(({ key, label }) => (
              <Field key={key} label={label}>
                <input type="number" min="0" className="input-cyber" value={claim[key] ?? 0} onChange={(e) => setClaim({ ...claim, [key]: e.target.value })} data-testid={`claim-rule-${key}`} />
              </Field>
            ))}
          </div>
          <button onClick={saveClaim} disabled={savingClaim} className="neon-btn px-6 py-2.5 mt-2" data-testid="claim-rules-save">
            {savingClaim ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : <><Save className="w-4 h-4" /> Save Claim Rules</>}
          </button>
        </div>
      )}

      <div className="mb-3 flex items-center gap-2 text-slate-300">
        <TrendingUp className="w-4 h-4" />
        <p className="text-sm">Custom promo rewards (optional — one-off perks that show up alongside the standard claim menu)</p>
      </div>

      <TableWrap>
        <thead><Tr><Th>Title</Th><Th>Type</Th><Th>Cost</Th><Th>Value</Th><Th>Claimed</Th><Th>Stock</Th><Th>Status</Th><Th className="text-right pr-4">Actions</Th></Tr></thead>
        <tbody>
          {rewards.length === 0 ? <EmptyRow colSpan={8} text="No custom promos yet" /> : rewards.map((r) => (
            <Tr key={r.id}>
              <Td>
                <p className="text-white font-semibold" data-testid={`reward-title-${r.id}`}>{r.title}</p>
                <p className="text-slate-500 text-xs max-w-xs truncate">{r.description}</p>
              </Td>
              <Td className="capitalize">{r.reward_type?.replace(/_/g, " ")}</Td>
              <Td><span className="inline-flex items-center gap-1" style={{ color: "#ffb020" }}><Zap className="w-3.5 h-3.5" />{r.points_required.toLocaleString()}</span></Td>
              <Td>{r.value ? `$${r.value}` : "—"}</Td>
              <Td>{r.claimed_count || 0}</Td>
              <Td>{r.stock_quantity == null ? "∞" : Math.max(0, r.stock_quantity - (r.claimed_count || 0))}</Td>
              <Td><StatusChip status={r.is_active ? "active" : "inactive"} /></Td>
              <Td className="text-right pr-4">
                <div className="inline-flex gap-2">
                  <button className="neon-outline-btn px-2.5 py-1.5 text-xs" onClick={() => openEdit(r)} data-testid={`reward-edit-${r.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                  <button className="neon-outline-btn px-2.5 py-1.5 text-xs" style={{ borderColor: "rgba(255,46,151,0.5)", color: "#ff2e97" }} onClick={() => remove(r)} data-testid={`reward-delete-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </Td>
            </Tr>
          ))}
        </tbody>
      </TableWrap>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? "Edit Promo Reward" : "Add Promo Reward"} testId="reward-modal">
        <Field label="Title"><input className="input-cyber" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="reward-form-title" /></Field>
        <Field label="Description"><textarea className="input-cyber min-h-[60px] resize-none" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="reward-form-desc" /></Field>
        <Field label="Type">
          <select className="input-cyber" value={form.reward_type} onChange={(e) => setForm({ ...form, reward_type: e.target.value })} data-testid="reward-form-type">
            {TYPES.map((t) => <option key={t.value} value={t.value} className="bg-slate-900">{t.label}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Points Cost"><input type="number" min="0" className="input-cyber" value={form.points_required} onChange={(e) => setForm({ ...form, points_required: e.target.value })} data-testid="reward-form-points" /></Field>
          <Field label="Value ($ or %)"><input type="number" min="0" className="input-cyber" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="reward-form-value" /></Field>
        </div>
        <Field label="Stock (leave blank for unlimited)"><input type="number" min="0" className="input-cyber" value={form.stock_quantity} onChange={(e) => setForm({ ...form, stock_quantity: e.target.value })} data-testid="reward-form-stock" /></Field>
        <label className="flex items-center gap-3 mb-5 cursor-pointer">
          <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="reward-form-active" />
          <span className="text-slate-200">Active</span>
        </label>
        <button onClick={create} disabled={saving} className="neon-btn w-full py-3" data-testid="reward-form-save">
          {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : (editing ? "Save Changes" : "Create Reward")}
        </button>
      </Modal>

      <Modal open={grantOpen} onClose={() => setGrantOpen(false)} title="Grant Points" testId="grant-modal">
        <Field label="Find User">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input className="input-cyber pl-9" placeholder="Search name or email" value={userQuery} onChange={(e) => setUserQuery(e.target.value)} data-testid="grant-user-search" />
          </div>
        </Field>
        <div className="max-h-52 overflow-y-auto scrollbar-thin mb-4 rounded-xl border border-white/10">
          {filteredUsers.length === 0 ? <p className="text-slate-500 text-sm p-4 text-center">No users found</p> : filteredUsers.map((u) => (
            <button key={u.id} onClick={() => setGrant({ ...grant, user_id: u.id })} data-testid={`grant-user-${u.id}`}
              className="w-full flex items-center justify-between px-4 py-2.5 text-left border-b border-white/5 last:border-0 transition-colors"
              style={{ background: grant.user_id === u.id ? "rgba(0,229,255,0.12)" : "transparent" }}>
              <span><span className="text-white font-semibold">{u.first_name} {u.last_name}</span> <span className="text-slate-500 text-xs block">{u.email}</span></span>
              <span style={{ color: "#ffb020" }} className="text-xs">{(u.points || 0).toLocaleString()} pts</span>
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Points (use negative to deduct)"><input type="number" className="input-cyber" value={grant.points} onChange={(e) => setGrant({ ...grant, points: e.target.value })} data-testid="grant-points-amount" /></Field>
          <Field label="Reason"><input className="input-cyber" placeholder="e.g. Event prize" value={grant.reason} onChange={(e) => setGrant({ ...grant, reason: e.target.value })} data-testid="grant-points-reason" /></Field>
        </div>
        <button onClick={doGrant} disabled={granting || !grant.user_id} className="neon-btn w-full py-3" data-testid="grant-points-submit">
          {granting ? <><Loader2 className="w-4 h-4 animate-spin" /> Granting...</> : <><UserPlus className="w-4 h-4" /> Grant Points</>}
        </button>
      </Modal>
    </div>
  );
}
